import { memo } from "react";
import Word from "./Word";
import type { StyleConfig } from "../state/styles";
import type { Word as WordType } from "../state/types";

interface ParagraphProps {
  words: WordType[];
  textStyle: StyleConfig["text"];
  /** Set only on the paragraph the keyboard cursor is in. */
  activeId?: string;
}

/**
 * One paragraph of the erasure, and the unit in which the page is redrawn.
 *
 * Two things here are performance, not presentation, and both were measured on
 * a 4,600-word article:
 *
 * `contain: paint` makes the paragraph its own paint container. Without it,
 * erasing one word had the browser re-record the display list for the whole
 * article — 12.6ms a mark, growing with the length of the article, and by far
 * the largest cost of erasing. With it, that falls below a millisecond. The
 * padding and the matching negative margin give the keyboard cursor's outline
 * somewhere to draw: paint containment clips to the box, and the outline sits
 * outside the word. They cancel out, so nothing moves.
 *
 * And the memo: the page is a new array on every mark, so without it React
 * rebuilt all 4,600 word elements to change one of them.
 */
const Paragraph = memo(
  ({ words, textStyle, activeId }: ParagraphProps) => (
    <div role="presentation" style={{ contain: "layout style" }}>
      {words.map((word) => (
        <Word
          key={word.id}
          word={word}
          textStyle={textStyle}
          isActive={word.id === activeId}
        />
      ))}
    </div>
  ),
  (before, after) =>
    before.textStyle === after.textStyle &&
    before.activeId === after.activeId &&
    before.words.length === after.words.length &&
    // Compared element-wise rather than by array identity: the page is rebuilt
    // on every mark, so every paragraph's array is new even when its words are
    // the very same objects.
    before.words.every((word, index) => word === after.words[index]),
);

Paragraph.displayName = "Paragraph";

export default Paragraph;
